import React from "react";
import { Box, Card, Stack, Typography, Divider } from "@mui/material";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import GroupsIcon from "@mui/icons-material/Groups";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === "" || value === 0) {
    return "-";
  }

  const count = Number(value);

  return key === "totalFollowUps" || key === "queries"
    ? String(count)
    : String(count).padStart(count < 10 ? 2 : 1, "0");
};

const sectionSx = (borderColor, background) => ({
  display: "grid",
  alignItems: "stretch",
  gap: 1.5,
  p: { xs: 1.5, md: 2 },
  borderRadius: 2,
  bgcolor: background,
});

const highlightStyles = {
  totalFollowUps: {
    bgcolor: "#fff3e8",
    background: "linear-gradient(to bottom, #fff3e8, #ffe0b2)",
    borderLeftColor: "#f57c00",
    boxShadow: "0 2px 4px rgba(187, 186, 185, 0.93)",
  },
  queries: {
    bgcolor: "#eaf8ff",
    borderLeftColor: "#0097a7",
   boxShadow: "0 2px 4px rgba(187, 186, 185, 0.93)",
  },
};

const cardSx = (key) => {
  const highlighted = highlightStyles[key];

  return {
    minHeight: { xs: 95, md: 100 },
    p: { xs: 1.5, md: 1.8 },
    borderRadius: 2,
    boxShadow: highlighted?.boxShadow || "none",
    bgcolor: highlighted?.bgcolor || "rgba(255, 255, 255, 0.7)",
    background: highlighted?.background,
    border: "1px solid #eef2f7",
    borderLeft: highlighted ? `4px solid ${highlighted.borderLeftColor}` : "1px solid #eef2f7",
  };
};

const getCardTextColor = (key, fallback) =>
  key === "totalFollowUps" || key === "queries" ? "#111827" : fallback;

const NecFollowUpSummaryCards = ({ summary = {}, category }) => {
  const categoryLabel = category || "Categories";

  const touchPointCards = [
    {
      key: "totalFollowUps",
      label: "Total touch points",
      icon: FormatListNumberedIcon,
      color: "#f57c00",
    },
    {
      key: "outgoingCalls",
      label: "Outgoing",
      icon: NorthEastIcon,
      color: "#2563eb",
    },
    {
      key: "incomingCalls",
      label: "Incoming",
      icon: SouthWestIcon,
      color: "#16a34a",
    },    
  ];

  const queryCards = [
    {
      key: "queries",
      label: categoryLabel,
      value:  summary.totalFollowUps,
      icon: QuestionAnswerIcon,
      color: "#0097a7",
    },
    {
      key: "completed",
      label: "Completed",
      icon: CheckCircleIcon,
      color: "#16a34a",
    },
    {
      key: "pending",
      label: "Pending",
      icon: HourglassBottomIcon,
      color: "#f59e0b",
    },
    {
      key: "onHold",
      label: "On Hold",
      icon: PauseCircleIcon,
      color: "#ef4444",
    },
    {
      key: "na",
      label: "NA",
      icon: RemoveCircleIcon,
      color: "#64748b",
    },
  ];

 const renderCard = ({ key, label, value, icon: Icon, color }) => (
  <Card key={key} sx={cardSx(key)}>
    <Stack
      spacing={1.3}
      sx={{
        height: "100%",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{display:"flex", flexDirection:"row", justifyContent:"space-between"}}>
        <Icon
          sx={{
            color,
            fontSize: key === "totalFollowUps" || key === "queries" ? 30 : 28,
          }}
        />

        <Typography
          sx={{
            fontSize: { xs: 22, md: 28 },
            lineHeight: 1,
            fontWeight: 700,
            color: getCardTextColor(key, "#1f2937"),
            alignContent:"space-evenly"
          }}
        >
          {formatValue(key, value ?? summary[key])}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1,
            color: getCardTextColor(key, "#64748b"),
            fontSize: 16,
            fontWeight: key === "totalFollowUps" || key === "queries" ? 700 : 500,
            lineHeight: 1.3,
          }}
          title={label}
        >
          {label}
        </Typography>
      </Box>
    </Stack>
  </Card>
);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          lg: "minmax(430px, 0.52fr) auto minmax(560px, 1fr)",
        },
        gap: 1.6,
      }}
    >
      <Box sx={{ ...sectionSx("#fdba74", "#fff7ed"), gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" } }}>
        {touchPointCards.map(renderCard)}
      </Box>

      <Divider orientation="vertical" variant="middle" flexItem/>

      <Box sx={{ ...sectionSx("#93c5fd", "#eff6ff"), gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" } }}>
        {queryCards.map(renderCard)}
      </Box>
    </Box>
  );
};

export default NecFollowUpSummaryCards;
